import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { hasLocizeTranslation } from 'lib/utils/locize'
import { withI18next } from 'lib/i18n/withI18next'
import Cart from 'lib/api/cart'
import cartDuck from 'components/cart/duck'
import cartItemsDuck from '../duck'
import styles from './styles.scss'

// Selection is driven by local Redux state once something's been
// toggled this session (selectedItemKeys), same as the per-item
// checkboxes -- see the comment in cart-product-checkbox/index.js.
// Before that, defaultSelectedKeys (CartItems' own read of each item's
// real is_selected, merged into cart.payload by Cart.getCart in
// lib/api/cart.js) is the fresh-load/refresh default instead of treating
// everything as selected. Toggling here write-throughs every affected
// item to POST /v6/carts/selectable-cart so the backend's own selection
// record matches, but the displayed total/checkout still reflect the
// whole cart.
const CartSelectAllHeader = ({
  allSelectableKeys,
  defaultSelectedKeys,
  selectedItemKeys,
  setItemsSelected,
  setMultipleItemsSelected,
  soldOutItemKeys,
  t,
}) => {
  const currentlySelected = selectedItemKeys ?? defaultSelectedKeys
  const selectedCount = currentlySelected.filter((key) =>
    allSelectableKeys.includes(key),
  ).length
  const allSelected = selectedCount === allSelectableKeys.length

  const handleChange = () => {
    const nowSelecting = !allSelected
    // Captured before the optimistic flip below, and passed to
    // setMultipleItemsSelected so it can put the selection back exactly as
    // it was if the batch is rejected. Read here rather than from
    // getState() inside the creator: by the time the creator runs, this
    // flip has already landed, so the creator can no longer see what the
    // selection was before it. `undefined` ("everything selected") is a
    // meaningful value to restore, so it's forwarded as-is.
    const selectionBeforeToggle = selectedItemKeys
    setItemsSelected(nowSelecting)
    // One batched selectable-cart call for every item that actually
    // changes, instead of one full setItemSelected round-trip each --
    // see setMultipleItemsSelected's own comment for why that mattered.
    // Sold-out items are skipped when re-selecting everything -- the
    // backend rejects select-product for them outright (confirmed live:
    // 400 "The specified product attribute is not found"), which failed
    // this whole batch, not just that one line, before this exclusion.
    const actions = allSelectableKeys
      .filter((key) => currentlySelected.includes(key) !== nowSelecting)
      .filter((key) => !(nowSelecting && soldOutItemKeys.includes(key)))
      .map((key) => {
        const [productID, productAttributeID] = key.split('-').map(Number)
        return {
          type: nowSelecting ? Cart.SELECT_ITEM : Cart.UNSELECT_ITEM,
          productID,
          productAttributeID,
        }
      })
    if (actions.length > 0) {
      setMultipleItemsSelected(
        actions,
        {},
        {
          selection: selectionBeforeToggle,
        },
      )
    }
  }

  return (
    <div className="cart-select-all">
      <style jsx>{styles}</style>
      <div className="cart-select-all__checkbox">
        <input
          type="checkbox"
          aria-label="Select all"
          checked={allSelected}
          onChange={handleChange}
        />
        <span className="cart-select-all__box" aria-hidden="true" />
      </div>
      <span className="cart-select-all__label">
        {hasLocizeTranslation(t, 'CART_SELECT_ALL', 'Select all')} (
        {selectedCount}/{allSelectableKeys.length})
      </span>
    </div>
  )
}

CartSelectAllHeader.defaultProps = {
  selectedItemKeys: undefined,
}

CartSelectAllHeader.propTypes = {
  allSelectableKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  defaultSelectedKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedItemKeys: PropTypes.arrayOf(PropTypes.string),
  setItemsSelected: PropTypes.func.isRequired,
  setMultipleItemsSelected: PropTypes.func.isRequired,
  soldOutItemKeys: PropTypes.arrayOf(PropTypes.string).isRequired,
  t: PropTypes.func.isRequired,
}

export default connect(
  (state) => ({
    selectedItemKeys: state.cartData.selectedItemKeys,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        setItemsSelected: cartItemsDuck.creators.setItemsSelected,
        setMultipleItemsSelected: cartDuck.creators.setMultipleItemsSelected,
      },
      dispatch,
    ),
)(withI18next()(CartSelectAllHeader))
